/**
 * ‘왜 이게 나오나’ 의 판정 기록을 사람이 읽는 순서와 말로.
 *
 * 해석 엔진(`@signageone/core` 의 `resolve`)은 **계산하는 차례**대로 기록을 남긴다 — 긴급,
 * 바로 보내기, 운영 시간, 화면·그룹 배정(그 안의 스케줄·플레이리스트), 그 위에 캠페인, 결과. 그런데
 * 사람이 묻는 것은 **무엇이 무엇을 이기는가**이고, 그 순서는 긴급 ▸ 바로 보내기 ▸ 운영 시간 ▸
 * 캠페인 ▸ 화면 배정 ▸ 그룹 배정이다. 계산 차례를 그대로 늘어놓았더니 배정이 캠페인보다 위에 적혀
 * "배정이 캠페인을 이긴다" 로 읽혔다(출하 검사 5.8 불합격). 보여 줄 때 이기는 순서로 세운다.
 */
import type { TraceEntry } from './api/client.js';

export const TRACE_STAGE_LABEL: Readonly<Record<string, string>> = {
  emergency: '긴급 오버라이드',
  direct: '바로 보내기',
  hours: '운영 시간',
  campaign: '캠페인',
  display: '화면 배정',
  group: '그룹 배정',
  schedule: '스케줄',
  playlist: '플레이리스트',
  result: '결과',
};

/** 이기는 순서. 스케줄·플레이리스트는 배정 안에서 고른 것이라 배정 뒤에 둔다. */
const RANK: Readonly<Record<string, number>> = {
  emergency: 0,
  direct: 1,
  // 운영 시간 밖이면 캠페인·배정보다 먼저 판정된다(긴급·바로 보내기는 쉬는 시간에도 나간다).
  hours: 1.5,
  campaign: 2,
  display: 3,
  group: 3,
  schedule: 4,
  playlist: 5,
  result: 6,
};

export function traceStageLabel(stage: string): string {
  return TRACE_STAGE_LABEL[stage] ?? stage;
}

/**
 * 이기는 순서로 세운다. 같은 단계 안에서는 엔진이 남긴 차례를 지킨다 — 모르는 단계는 결과
 * 바로 앞에 둔다(새 단계가 생겨도 사라지지 않게).
 */
export function traceInPriorityOrder(trace: readonly TraceEntry[]): TraceEntry[] {
  // 모르는 단계는 플레이리스트(5)와 결과(6) 사이.
  const rank = (stage: string): number => RANK[stage] ?? 5.5;
  return trace
    .map((entry, index) => ({ entry, index }))
    .sort((left, right) => rank(left.entry.stage) - rank(right.entry.stage) || left.index - right.index)
    .map(({ entry }) => entry);
}
